import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useSignUp } from '@/api';
import { SignUpBody } from '@/api/types';
import { Loader } from '@/components/Loader/Loader.tsx';
import { Button } from '@/components/shared/Button';
import { Checkbox } from '@/components/shared/Checkbox/Checkbox.tsx';
import { Input } from '@/components/shared/Input/Input.tsx';
import { SignUpSchema } from '@/components/shared/Modal/content/sign-up/constants.ts';
import { MainConfig } from '@/config/MainConfig.ts';
import { numberMask, numberPlaceholder, strFormatChars, strMask } from '@/constants';
import { useAuthStore } from '@/store';
import { formatNumber, isMobileApp } from '@/utils';

import cn from './styles.module.scss';

export type SignUpFormData = Omit<SignUpBody, 'msisdn'> & { msisdn: string };

export const ModalSignUpContent = ({
    openSignIn,
}: {
    openSignIn: (signingUp: boolean) => void;
}) => {
    const { msisdn, setMsisdn } = useAuthStore();

    const {
        control,
        getValues,
        setError,
        clearErrors,
        trigger,
        setValue,
        handleSubmit,
        formState,
    } = useForm<SignUpFormData>({
        defaultValues: {
            msisdn,
            firstname: '',
            lastname: '',
            agreement: false,
        },
        resolver: zodResolver(SignUpSchema),
    });

    const { mutateAsync: signUp, isPending } = useSignUp();

    const handleOpenSignUp = () => {
        openSignIn(false);
    };

    const onSubmit = async (data: SignUpFormData) => {
        const formattedNumber = formatNumber(data.msisdn);
        const trackingParamsStr = localStorage.getItem('trackingParams');
        const formattedData: SignUpBody = {
            ...data,
            msisdn: formattedNumber,
            trackingParams: trackingParamsStr ? JSON.parse(trackingParamsStr) : undefined,
        };
        const errors = await signUp(formattedData);
        const isExistingUser = errors?.find(error => error.type === 'user_already_exists');
        const isNumberInvalid = errors?.find(
            error => error.type === 'not_valid' && error.property === 'msisdn'
        );
        if (errors) {
            if (isExistingUser) {
                setError('msisdn', { message: 'Пользователь с таким номером уже существует' });
            }

            if (isNumberInvalid) {
                setError('msisdn', { message: 'Неверный формат номера' });
            }
            return;
        }

        localStorage.removeItem('trackingParams');
        setMsisdn(getValues('msisdn'));
        handleOpenSignUp();
    };

    useEffect(() => {
        setMsisdn('');
    }, []);

    return (
        <div className={cn.signUpContent}>
            <div className={cn.modalHeader}>
                <h2>Регистрация</h2>
                <p>
                    Хочешь пользоваться мобильной связью бесплатно? Введи данные и начни
                    зарабатывать!
                </p>
            </div>
            <div className={cn.modalForm}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className={cn.modalFormInputContainer}>
                        <div className={cn.inputWrapper}>
                            <Controller
                                render={({
                                    field: { value, onChange, onBlur },
                                    fieldState: { error },
                                }) => (
                                    <Input
                                        mask={strMask}
                                        maskChar=""
                                        formatChars={strFormatChars}
                                        label="Имя*"
                                        error={!!error?.message}
                                        helperText={error?.message}
                                        placeholder="Ввести имя"
                                        value={value}
                                        onChange={e => {
                                            onChange(e);
                                            trigger('firstname');
                                        }}
                                        onBlur={() => {
                                            onBlur();
                                            trigger('firstname');
                                        }}
                                        onClear={e => {
                                            e.preventDefault();
                                            clearErrors('firstname');
                                            setValue('firstname', '');
                                            trigger('firstname');
                                        }}
                                    />
                                )}
                                name="firstname"
                                control={control}
                            />
                        </div>
                        <div className={cn.inputWrapper}>
                            <Controller
                                render={({
                                    field: { value, onChange, onBlur },
                                    fieldState: { error },
                                }) => (
                                    <Input
                                        mask={strMask}
                                        maskChar=""
                                        formatChars={strFormatChars}
                                        label="Фамилия*"
                                        error={!!error?.message}
                                        helperText={error?.message}
                                        placeholder="Ввести фамилию"
                                        value={value}
                                        onChange={e => {
                                            onChange(e);
                                            trigger('lastname');
                                        }}
                                        onBlur={() => {
                                            onBlur();
                                            trigger('lastname');
                                        }}
                                        onClear={e => {
                                            e.preventDefault();
                                            clearErrors('lastname');
                                            setValue('lastname', '');
                                            trigger('lastname');
                                        }}
                                    />
                                )}
                                name="lastname"
                                control={control}
                            />
                        </div>
                        <div className={cn.inputWrapper}>
                            <Controller
                                render={({
                                    field: { value, onChange, onBlur },
                                    fieldState: { error },
                                }) => (
                                    <Input
                                        mask={numberMask}
                                        type="tel"
                                        label="Номер телефона*"
                                        error={!!error?.message}
                                        helperText={error?.message}
                                        placeholder={numberPlaceholder}
                                        value={value}
                                        onChange={e => {
                                            onChange(e);
                                            trigger('msisdn');
                                        }}
                                        onBlur={() => {
                                            onBlur();
                                            trigger('msisdn');
                                        }}
                                        onClear={e => {
                                            e.preventDefault();
                                            clearErrors('msisdn');
                                            setValue('msisdn', '');
                                            trigger('msisdn');
                                        }}
                                    />
                                )}
                                name="msisdn"
                                control={control}
                            />
                        </div>
                        <Controller
                            render={({ field: { onChange, onBlur } }) => (
                                <Checkbox onChange={onChange} onBlur={onBlur} />
                            )}
                            name="agreement"
                            control={control}
                        />
                    </div>

                    <div className={cn.modalFormBtnContainer}>
                        {isPending ? (
                            <Loader mini />
                        ) : (
                            <Button disabled={!formState.isValid}>Получить код</Button>
                        )}
                        <Button
                            className={cn.signInBtn}
                            onClick={handleOpenSignUp}
                            background="transparent"
                        >
                            Есть аккаунт? Войти
                        </Button>
                    </div>
                </form>
            </div>
            {!isMobileApp() && (
                <div className={cn.modalFooter}>
                    <h3>Удобнее в приложении</h3>
                    <div className={cn.modalFooterIconContainer}>
                        <a
                            target="_blank"
                            className={cn.imgWrapper}
                            href={MainConfig.appLink.googlePlay}
                            rel="noreferrer"
                        >
                            <img src="/images/icons/mobile-app/google-play.svg" alt="" />
                        </a>
                        <a
                            target="_blank"
                            className={cn.imgWrapper}
                            href={MainConfig.appLink.appStore}
                            rel="noreferrer"
                        >
                            <img src="/images/icons/mobile-app/app-store.svg" alt="" />
                        </a>
                        <a
                            target="_blank"
                            className={cn.imgWrapper}
                            href={MainConfig.appLink.apk}
                            rel="noreferrer"
                        >
                            <img src="/images/icons/mobile-app/apk.svg" alt="" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};
